import React from 'react';

import { Form, FormInstance } from '@ui-library/form';
import { FormConfig, FieldProps, FieldConfigMap } from '@api';

import { TextField, SelectField, SwitchField, TextAreaField, SecretTextField } from './components';

type PreRequisite = NonNullable<FieldConfigMap[keyof FieldConfigMap]['preRequisiteField']>;

interface LightFormBuilderProps {
  formConfig: FormConfig;
  form: FormInstance;
  variant?: 'next';
  // Fires on any field change. Used by data-retention to invalidate a prior
  // connection test when the config is edited.
  onValuesChange?: () => void;
}

const fieldComponentMap = {
  textField: TextField,
  textAreaField: TextAreaField,
  selectField: SelectField,
  switchField: SwitchField,
} as const;

const resolveFieldComponent = (fieldConfig: FieldConfigMap[keyof FieldConfigMap]) => {
  if (fieldConfig.component === 'textField' && fieldConfig.secret) {
    return SecretTextField;
  }
  return fieldComponentMap[fieldConfig.component];
};

/**
 * Renders its child only while the prerequisite field holds the required value.
 * Only fields that declare a `preRequisiteField` are wrapped in this, so the
 * per-field `useWatch` subscription is confined to the forms that need it.
 */
const ConditionalField: React.FC<{
  preRequisite: PreRequisite;
  form: FormInstance;
  children: React.ReactNode;
}> = ({ preRequisite, form, children }) => {
  const watchedValue = Form.useWatch(preRequisite.key, form);

  return watchedValue === preRequisite.value ? <>{children}</> : null;
};

export const LightFormBuilder: React.FC<LightFormBuilderProps> = ({
  form,
  formConfig,
  variant,
  onValuesChange,
}) => (
  <Form
    variant={variant}
    form={form}
    size="large"
    layout="vertical"
    onValuesChange={onValuesChange}
  >
    {formConfig.fields.map((fieldConfig) => {
      const FieldComponent = resolveFieldComponent(fieldConfig) as React.FC<
        FieldProps<keyof FieldConfigMap>
      >;

      if (!FieldComponent) {
        return null;
      }

      const key = Array.isArray(fieldConfig.key) ? fieldConfig.key.join('.') : fieldConfig.key;
      const preRequisite = fieldConfig.preRequisiteField;

      return preRequisite ? (
        <ConditionalField key={key} preRequisite={preRequisite} form={form}>
          <FieldComponent fieldConfig={fieldConfig} />
        </ConditionalField>
      ) : (
        <FieldComponent key={key} fieldConfig={fieldConfig} />
      );
    })}
  </Form>
);
